import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";

const LEADERBOARD_SCOPE_TABS = [
    { id: "global", label: "Global" },
    { id: "country", label: "Country" },
    { id: "friends", label: "Friends" },
];

export default function LeaderboardTabs({
    scopeTab,
    onScopeTabClick,
}) {
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "stretch", md: "center" },
                justifyContent: "flex-start",
                flexWrap: { xs: "nowrap", md: "wrap" },
                gap: { xs: "0.75rem", md: "1rem" },
                mt: 0,
                width: "100%",
            }}
        >
            <Box
                role="tablist"
                aria-label="Leaderboard scope"
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: { xs: "0.35rem", sm: "0.5rem" },
                    flex: { xs: "0 0 auto", md: "1 1 auto" },
                    minWidth: 0,
                    maxWidth: { xs: "100%", md: "fit-content" },
                    flexWrap: "nowrap",
                    p: 0,
                    border: 0,
                    background: "transparent",
                }}
            >
                {LEADERBOARD_SCOPE_TABS.map(({ id, label }) => {
                    const active = scopeTab === id;
                    return (
                        <Box
                            key={id}
                            role="tab"
                            aria-selected={active}
                            id={`leaderboard-tab-${id}`}
                            component="button"
                            type="button"
                            onClick={() => onScopeTabClick(id)}
                            sx={{
                                position: "relative",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "2.35rem",
                                px: { xs: "0.85rem", sm: "1.05rem" },
                                py: "0.55rem",
                                m: 0,
                                border: active
                                    ? `1px solid ${alpha(colors.accent.main, 0.14)}`
                                    : "1px solid transparent",
                                borderRadius: "9px",
                                background: active
                                    ? "linear-gradient(180deg, rgba(255,255,255,0.055), rgba(255,255,255,0.025))"
                                    : "transparent",
                                boxShadow: active
                                    ? "inset 0 1px 0 rgba(255,255,255,0.045), 0 4px 14px rgba(0,0,0,0.16)"
                                    : "none",
                                cursor: "pointer",
                                font: "inherit",
                                boxSizing: "border-box",
                                outline: 0,
                                transition:
                                    "background-color 120ms ease-out, border-color 120ms ease-out, box-shadow 120ms ease-out",
                                "&:hover": {
                                    background: active
                                        ? "linear-gradient(180deg, rgba(255,255,255,0.065), rgba(255,255,255,0.03))"
                                        : "rgba(255,255,255,0.032)",
                                },
                                "&:hover .MuiTypography-root": {
                                    color: active
                                        ? colors.text.primary
                                        : colors.text.secondary,
                                },
                                "&:focus-visible": {
                                    borderColor: alpha(colors.accent.soft, 0.52),
                                    boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.12)}`,
                                },
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    left: "50%",
                                    bottom: "0.22rem",
                                    width: active ? "1.1rem" : 0,
                                    height: "1px",
                                    borderRadius: "999px",
                                    background: active
                                        ? `linear-gradient(90deg, transparent, ${colors.accent.soft}, transparent)`
                                        : "transparent",
                                    transform: "translateX(-50%)",
                                    opacity: active ? 0.9 : 0,
                                    transition: "width 120ms ease-out, opacity 100ms ease-out",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    position: "relative",
                                    zIndex: 1,
                                    fontSize: { xs: "0.69rem", sm: "0.75rem" },
                                    fontWeight: active ? 680 : 560,
                                    letterSpacing: "0.105em",
                                    textTransform: "uppercase",
                                    color: active
                                        ? colors.text.primary
                                        : colors.text.muted,
                                    transition: "color 110ms ease-out, opacity 110ms ease-out",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
